// src/contexts/AuthContext.js - VERSÃO HÍBRIDA (LOCAL + EXTERNO)
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const NIVEIS = { EXPEDICAO: 1, LABORATORIO: 2, ADMINISTRADOR: 3 };

// ✅ CONFIGURAÇÃO SIMPLIFICADA - PRIORIZA VARIÁVEL DE AMBIENTE
const getApiBaseUrl = () => {
  // 1) Sempre usar REACT_APP_API_URL se estiver definida
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 2) Auto-detectar baseado no hostname atual
  const hostname = window.location.hostname;

  // 3) Configurações por ambiente
  if (hostname === 'admin.inholding.com.br' || hostname.includes('vercel.app')) {
    // Em produção (domínio próprio) e Vercel: usar proxy '/api' (Vercel rewrites)
    return '/api';
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168')) {
    return `http://${hostname}:3003/api`;
  }

  // 4) Fallback - usar API absoluta ao invés de localhost
  return 'https://api-admin.inholding.com.br/api';
};

const API_BASE_URL = getApiBaseUrl();
const ABSOLUTE_API_BASE_URL = 'https://api-admin.inholding.com.br/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ SISTEMA DE HEALTH CHECK
  const [apiStatus, setApiStatus] = useState('checking');

  const login = async (usuario, senha) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos timeout
      
      const requestData = { usuario, senha };
      
      let response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Fallback: se estiver usando proxy '/api' e retornar 404, tenta a API absoluta
      if (!response.ok && response.status === 404 && API_BASE_URL === '/api') {
        console.warn('↩️  Login 404 via /api. Tentando fallback direto na API absoluta...');
        response = await fetch(`${ABSOLUTE_API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData),
          signal: controller.signal
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // Corrigir extração dos dados do backend
      if (data.success && data.dados && data.dados.user && data.dados.token) {
        const userDataWithToken = { ...data.dados.user, token: data.dados.token };
        setUser(userDataWithToken);
        localStorage.setItem('user', JSON.stringify(userDataWithToken));
        setApiStatus('connected');
        return { success: true };
      }
      setApiStatus('error');
      return { success: false, message: data.message };
    } catch (error) {
      console.error('❌ Erro no login:', error);
      setApiStatus('error');
      
      // Mensagens de erro mais específicas
      if (error.name === 'AbortError') {
        return { success: false, message: 'Timeout: Servidor não respondeu em 10 segundos' };
      } else if (error.message.includes('Failed to fetch')) {
        return { success: false, message: 'Erro de conexão: Verifique se o servidor está rodando' };
      } else {
        return { success: false, message: `Erro de conexão: ${error.message}` };
      }
    }
  };

  const logout = () => { 
    setUser(null); 
    localStorage.removeItem('user'); 
    setApiStatus('disconnected');
  };

  const hasPermission = (nivelRequerido) => user ? user.nivel >= nivelRequerido : false;

  const apiCall = async (endpoint, options = {}) => {
    let currentEndpoint = endpoint;
    let url = `${API_BASE_URL}${currentEndpoint}`;
    const tryAbsoluteFallback = async (originalResponse) => {
      if (!originalResponse.ok && originalResponse.status === 404 && API_BASE_URL === '/api') {
        console.warn(`↩️  404 em ${currentEndpoint} via /api. Tentando fallback na API absoluta...`);
        const absUrl = `${ABSOLUTE_API_BASE_URL}${currentEndpoint}`;
        const resp = await fetch(absUrl, {
          ...options,
          headers: {
            ...baseHeaders,
            ...options.headers,
            ...authHeader
          }
        });
        // atualiza URL para logs subsequentes
        url = absUrl;
        return resp;
      }
      return originalResponse;
    };
    const storedUser = localStorage.getItem('user');
    const token = storedUser ? JSON.parse(storedUser).token : null;
    const authHeader = token ? { 'Authorization': `Bearer ${token}` } : {};
    // Evitar preflight desnecessário: só enviar Content-Type quando houver body ou método não-GET
    const shouldSendJson = options.body || (options.method && options.method !== 'GET');
    const baseHeaders = shouldSendJson ? { 'Content-Type': 'application/json' } : {};
    
    try {
  let response = await fetch(url, {
        ...options,
        headers: {
          ...baseHeaders,
          ...options.headers,
          ...authHeader
        }
      });

  // Se 404 usando proxy '/api', tenta a API absoluta
  response = await tryAbsoluteFallback(response);
      
      // Fallback automático em desenvolvimento: tentar rotas antigas se -new não existir
      if (!response.ok && response.status === 404) {
        const isRelatoriosNew = currentEndpoint.startsWith('/relatorios-new');
        const isProdutividadeNew = currentEndpoint.startsWith('/produtividade-new');
        if (isRelatoriosNew || isProdutividadeNew) {
          const legacyEndpoint = isRelatoriosNew
            ? currentEndpoint.replace('/relatorios-new', '/relatorios')
            : currentEndpoint.replace('/produtividade-new', '/produtividade');
          const legacyUrl = `${API_BASE_URL}${legacyEndpoint}`;
          console.warn(`↩️  404 em ${currentEndpoint}. Tentando fallback: ${legacyEndpoint}`);
          response = await fetch(legacyUrl, {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              ...options.headers,
              ...authHeader
            }
          });
          currentEndpoint = legacyEndpoint;
          url = legacyUrl;
        }
      }

  if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `Erro HTTP! Status: ${response.status}`
        }));

        // Sessão expirada/ausente: limpar o usuário e redirecionar para o login.
        // O ProtectedRoute observa user === null e manda para /login automaticamente,
        // então a página corrente não fica travada num "Erro ao carregar".
        if (response.status === 401 &&
            (errorData.code === 'INVALID_TOKEN' || errorData.code === 'NO_TOKEN')) {
          logout();
          throw new Error('Sessão expirada. Faça login novamente.');
        }

        const msg = errorData.detail
          ? `${errorData.message}: ${errorData.detail}`
          : (errorData.message || `Erro HTTP ${response.status}`);
        throw new Error(msg);
      }
      
      setApiStatus('connected');
      return response.json();
    } catch (error) {
      console.error(`❌ Erro na API ${endpoint}:`, error);
      setApiStatus('error');
      throw error;
    }
  };

  // ✅ Helper: tenta endpoint primário e, se 404, tenta fallback
  const apiCallWithFallback = async (primaryEndpoint, fallbackEndpoint, options = {}) => {
    try {
      return await apiCall(primaryEndpoint, options);
    } catch (error) {
      const msg = String(error?.message || '');
      const is404 = msg.includes('404') || msg.includes('não foi encontrada');
      if (is404 && fallbackEndpoint) {
        console.warn(`404 em ${primaryEndpoint}. Tentando fallback: ${fallbackEndpoint}`);
        return await apiCall(fallbackEndpoint, options);
      }
      throw error;
    }
  };

  // ✅ HEALTH CHECK DA API
  const checkApiHealth = async () => {
    try {
      console.log('🔍 Verificando saúde da API...');
      // Requisição simples (sem Content-Type) para evitar preflight
      let response = await fetch(`${API_BASE_URL}/health`);
      if (!response.ok && response.status === 404 && API_BASE_URL === '/api') {
        console.warn('↩️  Health 404 via /api. Tentando fallback direto na API absoluta...');
        response = await fetch(`${ABSOLUTE_API_BASE_URL}/health`);
      }
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ API Health OK:', data);
        setApiStatus('connected');
        return true;
      } else {
        console.warn('⚠️ API Health Check falhou:', response.status);
        setApiStatus('error');
        return false;
      }
    } catch (error) {
      console.error('❌ API Health Check erro:', error);
      setApiStatus('error');
      return false;
    }
  };

  const api = {
    // ============================================================================
    // LINHA DO TEMPO
    // ============================================================================
    buscarLinhaTempo: (termo) => apiCall(`/linha-tempo/${termo}`),

    // ============================================================================
    // FUNCIONÁRIOS
    // ============================================================================
    listarFuncionarios: () => apiCall('/funcionarios'),
    criarFuncionario: (dados) => apiCall('/funcionarios', { 
      method: 'POST', 
      body: JSON.stringify(dados) 
    }),
    atualizarFuncionario: (id, dados) => apiCall(`/funcionarios/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(dados) 
    }),
    excluirFuncionario: (id) => apiCall(`/funcionarios/${id}`, {
      method: 'DELETE'
    }),

    // ============================================================================
    // FILIAIS (filial de estoque das requisições — fc12100.cdfile)
    // ============================================================================
    listarFiliais: () => apiCallWithFallback('/relatorios-new/filiais', '/relatorios/filiais'),

    // ============================================================================
    // SETORES
    // ============================================================================
    listarSetores: () => apiCall('/setores'),
    criarSetor: (dados) => apiCall('/setores', { 
      method: 'POST', 
      body: JSON.stringify(dados) 
    }),
    atualizarSetor: (id, dados) => apiCall(`/setores/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(dados) 
    }),
    excluirSetor: (id) => apiCall(`/setores/${id}`, { 
      method: 'DELETE' 
    }),

    // ============================================================================
    // USUÁRIOS
    // ============================================================================
    listarUsuarios: () => apiCall('/usuarios'),
    criarUsuario: (dados) => apiCall('/usuarios', { 
      method: 'POST', 
      body: JSON.stringify(dados) 
    }),
    atualizarUsuario: (id, dados) => apiCall(`/usuarios/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(dados) 
    }),
    excluirUsuario: (id) => apiCall(`/usuarios/${id}`, { 
      method: 'DELETE' 
    }),

    // ============================================================================
    // PRODUTIVIDADE
    // ============================================================================
    produtividadeGeral: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.periodo) queryParams.append('periodo', params.periodo);
      
      const endpoint = '/produtividade/geral' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Produtividade Geral (Requisições Únicas)');
      return apiCall(endpoint);
    },

    // Funcionários por setor (case-insensitive) com período
    setorFuncionarios: (params = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const endpoint = '/produtividade/setor/funcionarios' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      console.log('🔄 API Call: Funcionários por Setor');
      return apiCall(endpoint);
    },

    produtividadeFuncionariosMes: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      // Adicionar filtros se fornecidos
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/relatorios-new/produtividade-funcionarios' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Relatório de Produtividade com Filtros');
      return apiCall(endpoint);
    },

    produtividadeFuncionario: (codigo, params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.periodo) queryParams.append('periodo', params.periodo);
      
      const endpoint = `/produtividade/funcionario/${codigo}` + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log(`🔄 API Call: Produtividade Individual (${codigo}) - Requisições Únicas`);
      return apiCall(endpoint);
    },

    // ============================================================================
    // ABA LABORATÓRIO - MÉTODOS
    // ============================================================================

    // Produtividade por setores do laboratório
    laboratorioSetores: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/laboratorio/setores' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Laboratório - Produtividade por Setores');
      return apiCall(endpoint);
    },

    // Produção geral do laboratório
    laboratorioProducaoGeral: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/laboratorio/producao-geral' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Laboratório - Produção Geral');
      return apiCall(endpoint);
    },

    // Horários de pico do laboratório
    laboratorioHorariosPico: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/laboratorio/horarios-pico' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Laboratório - Horários de Pico');
      return apiCall(endpoint);
    },

    // Crescimento vs mês passado
    laboratorioCrescimento: () => {
      console.log('🔄 API Call: Laboratório - Crescimento vs Mês Passado');
      return apiCall('/produtividade/laboratorio/crescimento');
    },

    // ============================================================================
    // ABA FUNCIONÁRIOS - MÉTODOS
    // ============================================================================

    // Top 5 funcionários
    funcionariosTop5: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/funcionarios/top5' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Funcionários - Top 5');
      return apiCall(endpoint);
    },

    // Métricas detalhadas de funcionários
    funcionariosMetricas: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/funcionarios/metricas' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Funcionários - Métricas Detalhadas');
      return apiCall(endpoint);
    },

    // Tempo entre leituras por funcionário
    funcionariosTempoEntreLeituras: (funcionario, params = {}) => {
      const queryParams = new URLSearchParams();
      queryParams.append('funcionario', funcionario);
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/funcionarios/tempo-entre-leituras' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log(`🔄 API Call: Funcionários - Tempo Entre Leituras (${funcionario})`);
      return apiCall(endpoint);
    },

    // Horários de pico por funcionário
    funcionariosHorariosPico: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/funcionarios/horarios-pico' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Funcionários - Horários de Pico');
      return apiCall(endpoint);
    },

    // ============================================================================
    // CAPACIDADE LAB
    // ============================================================================
    capacidadeLab: async (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.codigos) queryParams.append('codigos', params.codigos);
      const sufixo = queryParams.toString() ? `?${queryParams.toString()}` : '';
      console.log('🔄 API Call: Capacidade LAB');
      try {
        return await apiCall('/capacidade' + sufixo);
      } catch (e) {
        console.warn('Capacidade LAB fallback /capacidade/lab', e.message);
        try {
          return await apiCall('/capacidade/lab' + sufixo);
        } catch (e2) {
          console.warn('Capacidade LAB fallback /capacidade-lab', e2.message);
          return apiCall('/capacidade-lab' + sufixo);
        }
      }
    },

    // ============================================================================
    // ABA SETOR - MÉTODOS
    // ============================================================================

    // Métricas detalhadas por setor
    setoresMetricas: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/setores/metricas' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Setores - Métricas Detalhadas');
      return apiCall(endpoint);
    },

    // Comparativo entre setores
    setoresComparativo: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/setores/comparativo' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      console.log('🔄 API Call: Setores - Comparativo');
      return apiCall(endpoint);
    },

    // ============================================================================
    // MÉTODOS AUXILIARES PARA FILTROS
    // ============================================================================

    // Buscar opções para filtros (setores e funcionários)
    buscarOpcoesFiltros: () => {
      console.log('🔄 API Call: Buscar Opções para Filtros');
      return Promise.all([
        apiCall('/setores'),
        apiCall('/funcionarios')
      ]).then(([setoresResponse, funcionariosResponse]) => {
        return {
          setores: setoresResponse?.dados || [],
          funcionarios: funcionariosResponse?.dados || []
        };
      }).catch(error => {
        console.error('❌ Erro ao buscar opções de filtros:', error);
        return { setores: [], funcionarios: [] };
      });
    },

    // ============================================================================
    // RELATÓRIOS
    // ============================================================================
    relatorioLeituras: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/leituras' + qs;
      const fallback = '/relatorios/leituras' + qs;
      
      console.log('🔄 API Call: Relatório Leituras');
      console.log('📋 Filtros aplicados:', filtros);
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioProdutividadeFuncionarios: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/produtividade-funcionarios' + qs;
      const fallback = '/relatorios/produtividade-funcionarios' + qs;
      
      console.log('🔄 API Call: Relatório Produtividade Funcionários');
      console.log('📋 Filtros aplicados:', filtros);
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioVolumeProducao: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        console.log(`🔍 Processando parâmetro ${key}:`, value, 'tipo:', typeof value);
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
          console.log(`✅ Adicionado ${key}=${value}`);
        } else {
          console.log(`❌ Ignorado ${key}=${value} (valor vazio)`);
        }
      });
      
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/volume-producao' + qs;
      const fallback = '/relatorios/volume-producao' + qs;
      
      console.log('🔄 API Call: Relatório Volume de Produção');
      console.log('📋 Filtros aplicados:', filtros);
      console.log('🔗 Query string final:', qs);
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioVolumeProducaoDistribuicao: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/volume-producao/distribuicao' + qs;
      const fallback = '/relatorios/volume-producao/distribuicao' + qs;
      
      console.log('🔄 API Call: Distribuição Volume de Produção');
      console.log('📋 Filtros aplicados:', filtros);
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioTabular: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/tabular' + qs;
      const fallback = '/relatorios/tabular' + qs;
      
      console.log('🔄 API Call: Relatório Tabular (dados por dia)');
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioTabularValidar: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/tabular/validar' + qs;
      const fallback = '/relatorios/tabular/validar' + qs;
      
      console.log('🔄 API Call: Relatório Tabular - Validação');
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioProducaoCapsulas: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/producao-capsulas' + qs;
      const fallback = '/relatorios/producao-capsulas' + qs;
      
      console.log('🔄 API Call: Relatório Produção de Cápsulas');
      console.log('📋 Filtros aplicados:', filtros);
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioPerformanceTempo: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/performance-tempo' + qs;
      const fallback = '/relatorios/performance-tempo' + qs;
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioEvolucaoMensal: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/evolucao-mensal' + qs;
      const fallback = '/relatorios/evolucao-mensal' + qs;
      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioSetores: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/produtividade-setores' + qs;
      const fallback = '/relatorios/produtividade-setores' + qs;
      return apiCallWithFallback(endpoint, fallback);
    },

    // ============================================================================
    // SISTEMA
    // ============================================================================
    healthCheck: () => apiCall('/health')
  };
  
  const getNivelNome = (nivel) => {
    const nomes = { 
      1: 'Expedição', 
      2: 'Laboratório', 
      3: 'Administrador' 
    };
    return nomes[nivel] || 'Desconhecido';
  };

  useEffect(() => {
    // Verificar usuário salvo
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try { 
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser); 
      } catch (error) { 
        console.error('❌ Erro ao parsear usuário do localStorage:', error);
        localStorage.removeItem('user'); 
      }
    }
    
    // Health check da API
    checkApiHealth();
    
    setLoading(false);
  }, []);

  const value = { 
    user, 
    loading, 
    login, 
    logout, 
    hasPermission, 
    api, 
    NIVEIS, 
    getNivelNome,
    apiStatus,
    checkApiHealth,
    API_BASE_URL // Expor para debug
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};
